import { Box, Button, Card, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import { useState } from "react";
import BackIcon from "components/AppIcons/BackIcon";
import FaiBox from "MuiComponents/FaiBox";
import FaiButton from "MuiComponents/FaiButton";
import FaiTypography from "MuiComponents/FaiTypography";
import Axios from "services/api/config";
import { approveReturnURL, rejectReturnURL } from "services/api/routes/Order";
import DynamicCardContent from "./DynamicCardContent";

export default function ReturnDetails({ orderData, setOrderDetails, onRefresh }) {
	const approvalStatus =
		orderData?.approvalStatus || orderData?.raw?.approvalStatus || "REQUESTED";
	const rejectionReason = orderData?.raw?.rejectionReason;
	const [busy, setBusy] = useState(false);

	const approve = async () => {
		setBusy(true);
		try {
			await Axios({ ...approveReturnURL(orderData.id) });
			onRefresh && (await onRefresh());
			setOrderDetails();
		} catch (e: any) {
			alert(e?.response?.data?.errors?.[0] || "Failed to approve the return");
		} finally {
			setBusy(false);
		}
	};

	const reject = async () => {
		const reason = window.prompt(
			"Reason for rejecting this return (shown to the customer):"
		);
		if (reason == null) return; // cancelled
		if (!reason.trim()) {
			alert("A rejection reason is required.");
			return;
		}
		setBusy(true);
		try {
			await Axios({ ...rejectReturnURL(orderData.id, reason.trim()) });
			onRefresh && (await onRefresh());
			setOrderDetails();
		} catch (e: any) {
			alert(e?.response?.data?.errors?.[0] || "Failed to reject the return");
		} finally {
			setBusy(false);
		}
	};

	const statusColor =
		approvalStatus === "APPROVED"
			? "success"
			: approvalStatus === "REJECTED"
			? "error"
			: "warning";

	const productData = {};
	// {
	// 	// title: "Product Details",
	// 	data: [
	// 		{ label: "Brand", value: "House of Fashion" },
	// 		{ label: "Product Name", value: "Brown Cardigan" },
	// 		{ label: "Product ID", value: "PRI1234" },
	// 		{ label: "Price", value: "Rs. 1232" },
	// 	],
	// 	imageUrl: "https://via.placeholder.com/150",
	// };

	const orderDetails = {
		title: "Order Details",
		data: [
			{ label: "Sub-order ID", value: orderData?.raw?.subOrderId || "-" },
			{ label: "Order Date", value: orderData?.date || "-" },
			{ label: "Quantity", value: `${orderData?.qty ?? 0} Pc` },
			{ label: "Amount", value: orderData?.price || "-" },
		],
	};

	const shippingData = {
		title: "Shipping Details",
		data: [
			{
				label: "Address",
				value: "42/1 Gandhi Road, New Delhi, India PIN: 110001",
			},
			{ label: "Shipping Partner", value: "Delhivery" },
			{ label: "Shipping ID", value: "#SHP127845211" },
		],
	};

	const paymentData = {
		title: "Payment Method",
		data: [{ label: "Payment Method", value: "Cash on Delivery" }],
	};

	return (
		<div className="space-y-2">
			<Card className="my-6 p-3 md:p-6 space-y-4">
				<Box className="flex justify-between items-center">
					<Button
						startIcon={<BackIcon />}
						onClick={() => setOrderDetails()}
						className="!p-1 text-theme"
					>
						Back to Orders
					</Button>
					<Box className="flex items-center gap-3">
						<Chip label={approvalStatus} color={statusColor as any} size="small" />
						{approvalStatus === "REQUESTED" && (
							<>
								<Button
									variant="contained"
									color="error"
									disabled={busy}
									onClick={reject}
									sx={{ color: "#fff", backgroundColor: "#DC2626", fontWeight: 600, "&:hover": { backgroundColor: "#b91c1c" }, "&.Mui-disabled": { backgroundColor: "#f3b4b4", color: "#fff" } }}
								>
									Reject
								</Button>
								<Button
									variant="contained"
									color="success"
									disabled={busy}
									onClick={approve}
									sx={{ color: "#fff", backgroundColor: "#00A743", fontWeight: 600, "&:hover": { backgroundColor: "#00913a" }, "&.Mui-disabled": { backgroundColor: "#a7e0bd", color: "#fff" } }}
								>
									{busy ? <CircularProgress size={18} /> : "Approve"}
								</Button>
							</>
						)}
					</Box>
				</Box>
				{approvalStatus === "REJECTED" && rejectionReason && (
					<Box className="p-3 rounded-lg bg-[#ffebe9]">
						<Typography className="!text-sm !font-medium !text-[#cf222e]">
							Rejected: {rejectionReason}
						</Typography>
					</Box>
				)}
				<Box>
					<p>Sub-order {orderData?.raw?.subOrderId || "-"}</p>
					<Box
						className="rounded-2xl p-2 md:p-4"
						sx={{ backgroundColor: "#F9E9DA" }}
					>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								{/* <DynamicCardContent className="flex justify-between" {...productData} /> */}
								<FaiBox className="!bg-[#F9E9DA] rounded-lg p-4 mb-4">
									<Card className="!rounded-lg">
										<FaiBox className="p-4">
											<Grid
												container
												spacing={2}
												className="!flex !items-center"
											>
												<Grid item xs={3}>
													<Typography className="!text-base !font-medium text-gray-500 !mb-1">
														Product Name
													</Typography>
													<Typography className="!text-lg !font-medium">
														{orderData?.productName || "-"}
													</Typography>
												</Grid>
												<Grid item xs={3}>
													<Typography className="!text-base !font-medium text-gray-500 !mb-1">
														Product Id
													</Typography>
													<Typography className="!text-lg !font-medium">
														{orderData?.raw?.productId || "-"}
													</Typography>
												</Grid>
												<Grid item xs={3}>
													<Typography className="!text-base !font-medium text-gray-500 !mb-1">
														Order Date
													</Typography>
													<Typography className="!text-lg !font-medium">
														{orderData?.date || "-"}
													</Typography>
												</Grid>
												<Grid item xs={3}>
													<Typography className="!text-base !font-medium text-gray-500 !mb-1">
														Price
													</Typography>
													<Typography className="!text-lg !font-medium">
														{orderData?.price || "-"}
													</Typography>
												</Grid>
											</Grid>
										</FaiBox>
									</Card>
								</FaiBox>
							</Grid>
						</Grid>
						<Grid container xs={12} spacing={2}>
							<Grid item xs={12} md={4}>
								<DynamicCardContent {...orderDetails} />
							</Grid>
							<Grid item xs={12} md={4}>
								<DynamicCardContent {...shippingData} />
							</Grid>
							<Grid item xs={12} md={4}>
								<DynamicCardContent {...paymentData} />
							</Grid>
							{/* <DynamicCardContent {...shippingData} />
							<DynamicCardContent {...paymentData} /> */}
						</Grid>
					</Box>
					<Box className="mt-6">
						<Typography className="!text-lg !font-medium">
							Reason of Return
						</Typography>
						<Box className="mt-2 p-4 rounded-lg bg-[#EBEBEB]">
							<Typography className="!text-lg !font-normal !text-gray-500">
								{orderData?.reason || "-"}
							</Typography>
						</Box>
					</Box>
					{Array.isArray(orderData?.raw?.returnImages) &&
						orderData.raw.returnImages.length > 0 && (
							<Box className="mt-6">
								<Typography className="!text-lg !font-medium">
									Product Photos
								</Typography>
								<Box className="mt-2 flex flex-wrap gap-3">
									{orderData.raw.returnImages.map((url: string, i: number) => (
										<a key={i} href={url} target="_blank" rel="noreferrer">
											<img
												src={url}
												alt={`return-${i}`}
												style={{
													width: 96,
													height: 96,
													objectFit: "cover",
													borderRadius: 8,
													border: "1px solid #e0e0e0",
												}}
											/>
										</a>
									))}
								</Box>
							</Box>
						)}
					<Box className="mt-6">
						<Typography className="!text-lg !font-medium">
							Issue Description
						</Typography>
						<Box className="mt-2 p-4 rounded-lg bg-[#EBEBEB]">
							<Typography className="!text-lg !font-normal !text-gray-500">
								{orderData?.reason || "-"}
							</Typography>
						</Box>
					</Box>
				</Box>
			</Card>
		</div>
	);
}
